// Item catalog for Nexus Forge.
//
// Adding an item is a config change here, not a code change.
// Per DECISIONS 2026-04-17 "shared/items.ts omits per-item maxStack":
// non-weapon items use balance.inventory.maxStackSize as the default
// stack limit. Phase 5 Step 10 amends that rule for weapons only:
// weaponType != null forces maxStack = 1 via `itemMaxStack` so durable
// per-instance rows are never coalesced by `addItem`. See DECISIONS
// 2026-04-19 "[Phase 5, Step 10] maxStack=1 rule for weapons".
//
// Type hierarchy (Phase 3):
//   BaseItemId      = keyof typeof items (all base IDs, raw + craftable)
//   CraftableBaseId = subset of BaseItemId whose instances appear in
//                     inventory with a quality tier suffix
//   ItemId          = BaseItemId | `${CraftableBaseId}:${QualityTier}`
//                     (the full inventory item ID domain)
//
// Raw resources (copper, silicon, ferrite, polymer) never carry quality
// suffixes. Crafted items always do when they enter inventory.

import type { WeaponCategory } from "./abilities.ts";
import { balance } from "./balance.ts";
import { parseItemId, type QualityTier } from "./quality.ts";

export type ItemCategory = "raw" | "crafted";

// `weaponType` tags a weapon's category for ability gear-gating
// (shared/abilities.ts `meetsAbilityRequirements`). Non-weapon items
// omit the field entirely (TS `undefined`). Phase 5 Step 9 introduces
// this alongside the first two weapon entries (rusty_knife,
// scout_pistol); Step 10 wires per-instance durability on top.

// `weaponType: null` on non-weapons unifies ItemDef's shape so callers
// can read `.weaponType` without a conditional branch. Phase 5 Step 10
// adds `maxDurability` — positive on weapon entries, null everywhere
// else. `maxDurability` drives the per-instance durability seed in
// `player-inventory.addItem` + workshop claim: when the item enters
// inventory the row's `durability_current` / `durability_max` columns
// are populated from this value.
export const items = {
  // --- Raw gatherable resources (tier 0, no quality suffix) ---
  copper: {
    id: "copper",
    name: "Copper Ore",
    sprite: "copper",
    color: 0xcc6633,
    tier: 0,
    category: "raw" as ItemCategory,
    weaponType: null as WeaponCategory | null,
    maxDurability: null as number | null,
  },
  silicon: {
    id: "silicon",
    name: "Silicon Crystal",
    sprite: "silicon",
    color: 0x448bcc,
    tier: 0,
    category: "raw" as ItemCategory,
    weaponType: null as WeaponCategory | null,
    maxDurability: null as number | null,
  },
  ferrite: {
    id: "ferrite",
    name: "Ferrite Ore",
    sprite: "ferrite",
    color: 0x9a6644,
    tier: 0,
    category: "raw" as ItemCategory,
    weaponType: null as WeaponCategory | null,
    maxDurability: null as number | null,
  },
  polymer: {
    id: "polymer",
    name: "Polymer",
    sprite: "polymer",
    color: 0x44cc99,
    tier: 0,
    category: "raw" as ItemCategory,
    weaponType: null as WeaponCategory | null,
    maxDurability: null as number | null,
  },

  // --- Tier-1 crafted items (produced by tier-1 recipes) ---
  copper_wire: {
    id: "copper_wire",
    name: "Copper Wire",
    sprite: "copper_wire",
    color: 0xddaa22,
    tier: 1,
    category: "crafted" as ItemCategory,
    weaponType: null as WeaponCategory | null,
    maxDurability: null as number | null,
  },
  polymer_plate: {
    id: "polymer_plate",
    name: "Polymer Plate",
    sprite: "polymer_plate",
    color: 0x44bb88,
    tier: 1,
    category: "crafted" as ItemCategory,
    weaponType: null as WeaponCategory | null,
    maxDurability: null as number | null,
  },

  // --- Tier-2 crafted items (produced by tier-2 recipes) ---
  basic_circuit: {
    id: "basic_circuit",
    name: "Basic Circuit",
    sprite: "basic_circuit",
    color: 0x3377dd,
    tier: 2, // contracts:allow-literal — item tier classification, not a tuning value
    category: "crafted" as ItemCategory,
    weaponType: null as WeaponCategory | null,
    maxDurability: null as number | null,
  },
  ferrite_core: {
    id: "ferrite_core",
    name: "Ferrite Core",
    sprite: "ferrite_core",
    color: 0xaa5533,
    tier: 2, // contracts:allow-literal — item tier classification, not a tuning value
    category: "crafted" as ItemCategory,
    weaponType: null as WeaponCategory | null,
    maxDurability: null as number | null,
  },
  signal_amplifier: {
    id: "signal_amplifier",
    name: "Signal Amplifier",
    sprite: "signal_amplifier",
    color: 0xbb44cc,
    tier: 2, // contracts:allow-literal — item tier classification, not a tuning value
    category: "crafted" as ItemCategory,
    weaponType: null as WeaponCategory | null,
    maxDurability: null as number | null,
  },

  // --- Tier-2 exotics (Phase 5 Step 13b) ---
  // Boss-drop-only. Not CraftableBaseId (no quality suffix); stackable
  // at the default maxStackSize. Phase 5's economy-gate materials —
  // Phase 6 recipes will consume them.
  quantum_filament: {
    id: "quantum_filament",
    name: "Quantum Filament",
    sprite: "quantum_filament",
    color: 0x66ffee,
    tier: 2, // contracts:allow-literal — item tier classification, not a tuning value
    category: "crafted" as ItemCategory,
    weaponType: null as WeaponCategory | null,
    maxDurability: null as number | null,
  },
  plasma_core: {
    id: "plasma_core",
    name: "Plasma Core",
    sprite: "plasma_core",
    color: 0xff7733,
    tier: 2, // contracts:allow-literal — item tier classification, not a tuning value
    category: "crafted" as ItemCategory,
    weaponType: null as WeaponCategory | null,
    maxDurability: null as number | null,
  },

  // --- Phase 6 Block B exotic crafted items (output of new tier-2 recipes) ---
  quantum_filament_brace: {
    id: "quantum_filament_brace",
    name: "Quantum Filament Brace",
    sprite: "quantum_filament_brace",
    color: 0x44eedd,
    tier: 2, // contracts:allow-literal — item tier classification
    category: "crafted" as ItemCategory,
    weaponType: null as WeaponCategory | null,
    maxDurability: null as number | null,
  },
  plasma_conduit: {
    id: "plasma_conduit",
    name: "Plasma Conduit",
    sprite: "plasma_conduit",
    color: 0xff9944,
    tier: 2, // contracts:allow-literal — item tier classification
    category: "crafted" as ItemCategory,
    weaponType: null as WeaponCategory | null,
    maxDurability: null as number | null,
  },
  reinforced_hull_plate: {
    id: "reinforced_hull_plate",
    name: "Reinforced Hull Plate",
    sprite: "reinforced_hull_plate",
    color: 0x8899aa,
    tier: 2, // contracts:allow-literal — item tier classification
    category: "crafted" as ItemCategory,
    weaponType: null as WeaponCategory | null,
    maxDurability: null as number | null,
  },

  // --- Consumable: repair kit (Phase 5 Step 10a) ---
  // Added to the catalog ahead of its recipe (Step 10b) because the
  // repairItem mechanic pre-flights inventory ownership via
  // isItemId → player-inventory.removeItem. Not a CraftableBaseId, so
  // instances store without a quality suffix. No weaponType.
  repair_kit: {
    id: "repair_kit",
    name: "Repair Kit",
    sprite: "repair_kit",
    color: 0xdd9944,
    tier: 1,
    category: "crafted" as ItemCategory,
    weaponType: null as WeaponCategory | null,
    maxDurability: null as number | null,
  },

  // --- Tier-1 weapons (Phase 5 Step 9) ---
  // No skill-gate, no craft recipe yet. Phase 5 wires equip + gear-gate
  // plumbing; Phase 6+ adds recipes and higher-tier weapons.
  rusty_knife: {
    id: "rusty_knife",
    name: "Rusty Knife",
    sprite: "rusty_knife",
    color: 0x888877,
    tier: 1,
    category: "crafted" as ItemCategory,
    weaponType: "melee" as WeaponCategory | null,
    maxDurability: 50 as number | null, // contracts:allow-literal — weapon durability datum
  },
  scout_pistol: {
    id: "scout_pistol",
    name: "Scout Pistol",
    sprite: "scout_pistol",
    color: 0x556677,
    tier: 1,
    category: "crafted" as ItemCategory,
    weaponType: "ranged" as WeaponCategory | null,
    maxDurability: 50 as number | null, // contracts:allow-literal — weapon durability datum
  },
} as const;

// -----------------------------------------------------------------------
// Base type: all item IDs as they appear in the item catalog.
// Raw resources use this directly; crafted items use this as the base
// part of a quality-suffixed FullItemId.
// -----------------------------------------------------------------------
export type BaseItemId = keyof typeof items;

// Items that can be crafted and thus carry a quality tier suffix in inventory.
// Defined explicitly (not inferred from recipes) to keep items.ts independent
// of recipes.ts and to avoid circular imports.
export type CraftableBaseId =
  | "copper_wire"
  | "polymer_plate"
  | "basic_circuit"
  | "ferrite_core"
  | "signal_amplifier"
  | "quantum_filament_brace"
  | "plasma_conduit"
  | "reinforced_hull_plate";

// Full item ID as it appears in inventory slots and wire messages:
//   - Raw resources:    "copper", "silicon", "ferrite", "polymer"
//   - Crafted items:    "copper_wire:common", "basic_circuit:fine", etc.
export type ItemId = BaseItemId | `${CraftableBaseId}:${QualityTier}`;

// Individual item descriptor. Discriminated by `id`.
export type ItemDef = (typeof items)[BaseItemId];

// -----------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------

const CRAFTABLE_IDS: ReadonlySet<string> = new Set<CraftableBaseId>([
  "copper_wire",
  "polymer_plate",
  "basic_circuit",
  "ferrite_core",
  "signal_amplifier",
  "quantum_filament_brace",
  "plasma_conduit",
  "reinforced_hull_plate",
]);

export function isCraftableBaseId(id: string): id is CraftableBaseId {
  return CRAFTABLE_IDS.has(id);
}

// Runtime narrowing helper for wire/storage boundaries.
// Accepts both raw IDs ("copper") and quality-suffixed IDs ("copper_wire:common").
export function isItemId(id: string): id is ItemId {
  if (id in items) return true;
  const { baseId, quality } = parseItemId(id);
  return baseId in items && isCraftableBaseId(baseId) && quality !== null;
}

// Returns the base item definition for any ItemId. Strips the quality suffix
// if present so callers get the catalog entry regardless of quality tier.
export function getItemDef(id: string): ItemDef | undefined {
  if (id in items) return items[id as BaseItemId];
  const { baseId } = parseItemId(id);
  return baseId in items ? items[baseId as BaseItemId] : undefined;
}

// Convenience re-export so consumers only need one import for crafted IDs.
export type { QualityTier } from "./quality.ts";

// -----------------------------------------------------------------------
// Stacking policy (Phase 5 Step 10)
// -----------------------------------------------------------------------

// Per-item maximum stack size used by player-inventory.addItem when
// deciding whether to coalesce into an existing row or allocate a new
// one. Weapons (weaponType != null) force maxStack = 1 so each crafted
// instance keeps its own durability row. Every other item falls back
// to the global `balance.inventory.maxStackSize` default.
export function itemMaxStack(itemId: string): number {
  const def = getItemDef(itemId);
  if (def !== undefined && def.weaponType !== null) return 1; // contracts:allow-literal — per-instance rule for weapons
  return balance.inventory.maxStackSize;
}

// Per-item maximum durability used by player-inventory.addItem to seed
// a freshly-inserted row's `durability_current` / `durability_max`
// columns. Items without a `maxDurability` entry return null, meaning
// "not durability-bearing" — addItem writes the `-1` sentinel on both
// durability columns and applyWeaponWear / repairItem reject them.
export function itemMaxDurability(itemId: string): number | null {
  const def = getItemDef(itemId);
  if (def === undefined) return null;
  // `maxDurability` is a union-wide field; some older entries may omit
  // it entirely — coerce through an index access + optional chain for
  // safety during schema evolution.
  const raw = (def as { maxDurability?: number | null }).maxDurability;
  return raw ?? null;
}
